import React, { createContext, useContext, useState, useEffect } from 'react';
import type { User } from 'firebase/auth';
import {
  signInWithGoogle,
  signInWithApple,
  signOutUser,
  onAuthChange,
  getIdToken,
  handleRedirectResult
} from '../config/firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isVerified: boolean;
  profileClaims: any[];
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [profileClaims, setProfileClaims] = useState<any[]>([]);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

  const fetchUserData = async (firebaseUser: User) => {
    try {
      const token = await firebaseUser.getIdToken();

      // Register/update user in our backend
      const registerResponse = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firebase_uid: firebaseUser.uid,
          email: firebaseUser.email,
          display_name: firebaseUser.displayName,
          photo_url: firebaseUser.photoURL,
        }),
      });

      if (!registerResponse.ok) {
        console.error('Failed to register user');
        return;
      }

      // Get user data with claims
      const meResponse = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (meResponse.ok) {
        const data = await meResponse.json();
        setIsVerified(data.user.is_verified || false);
        setProfileClaims(data.profile_claims || []);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    // Check for redirect result on mount
    handleRedirectResult().then(async (user) => {
      if (user) {
        await fetchUserData(user);
      }
    });

    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await fetchUserData(firebaseUser);
      } else {
        setUser(null);
        setIsVerified(false);
        setProfileClaims([]);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const handleSignInWithGoogle = async () => {
    try {
      const firebaseUser = await signInWithGoogle();
      if (firebaseUser) {
        await fetchUserData(firebaseUser);
      }
    } catch (error) {
      console.error('Google sign-in failed:', error);
      throw error;
    }
  };

  const handleSignInWithApple = async () => {
    try {
      const firebaseUser = await signInWithApple();
      if (firebaseUser) {
        await fetchUserData(firebaseUser);
      }
    } catch (error) {
      console.error('Apple sign-in failed:', error);
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      setIsVerified(false);
      setProfileClaims([]);
    } catch (error) {
      console.error('Sign out failed:', error);
      throw error;
    }
  };

  const refreshUserData = async () => {
    if (user) {
      await fetchUserData(user);
    }
  };

  const value = {
    user,
    loading,
    isVerified,
    profileClaims,
    signInWithGoogle: handleSignInWithGoogle,
    signInWithApple: handleSignInWithApple,
    signOut: handleSignOut,
    getToken: getIdToken,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
